<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <!-- Start:: Name Input -->
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.couponName')"
                v-model.trim="filterOptions.name"
                :maxlength="100"
              />
              <!-- End:: Name Input -->

              <!-- Start:: Expiration Status Input -->
              <base-select-input
                col="4"
                :optionsList="expirationStatusOptions"
                :placeholder="$t('PLACEHOLDERS.expirationStatus')"
                v-model="filterOptions.expirationStatus"
              />
              <!-- End:: Expiration Status Input -->

              <!-- Start:: Status Input -->
              <base-select-input
                col="4"
                :optionsList="statusOptions"
                :placeholder="$t('PLACEHOLDERS.status')"
                v-model="filterOptions.is_active"
              />
              <!-- End:: Status Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>{{ $t("TITLES.couponsManagement") }}</h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <router-link to="/coupons/create">
            {{ $t("BUTTONS.addCoupon") }}
          </router-link>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ $t("TABLES.noData") }}
        </template>
        <!-- End:: No Data State -->

        <!-- Start:: Serial Number -->
        <template v-slot:[`item.id`]="{ item, index }">
          <p>
            {{
              (paginations.current_page - 1) * paginations.items_per_page +
              index +
              1
            }}
          </p>
        </template>
        <!-- End:: Serial Number -->

        <!-- Start:: Coupon Name -->
        <template v-slot:[`item.name`]="{ item }">
          <p v-if="!item.name">-</p>
          <p v-else>{{ item.name }}</p>
        </template>
        <!-- End:: Coupon Name -->

        <!-- Start:: Code -->
        <template v-slot:[`item.code`]="{ item }">
          <p v-if="!item.code">-</p>
          <p v-else>{{ item.code }}</p>
        </template>
        <!-- End:: Code -->

        <!-- Start:: Start Date -->
        <template v-slot:[`item.starts_at`]="{ item }">
          <p v-if="!item.starts_at">-</p>
          <p v-else>{{ formatDate(item.starts_at) }}</p>
        </template>
        <!-- End:: Start Date -->

        <!-- Start:: End Date -->
        <template v-slot:[`item.ends_at`]="{ item }">
          <p v-if="!item.ends_at">-</p>
          <p v-else>{{ formatDate(item.ends_at) }}</p>
        </template>
        <!-- End:: End Date -->

        <!-- Start:: Usage Count -->
        <template v-slot:[`item.usage_count`]="{ item }">
          <p>{{ item.usage_count || 0 }}</p>
        </template>
        <!-- End:: Usage Count -->

        <!-- Start:: Users -->
        <template v-slot:[`item.users`]="{ item }">
          <button
            class="btn_view_users"
            @click="selectViewUsers(item)"
            :disabled="!item.usage_count || item.usage_count === 0"
            :title="$t('BUTTONS.viewUsers')"
          >
            <i class="fal fa-users"></i>
            <span>{{ item.users_count || 0 }}</span>
          </button>
        </template>
        <!-- End:: Users -->

        <!-- Start:: Expiration Status -->
        <template v-slot:[`item.expiration_status`]="{ item }">
          <p v-if="!item.starts_at || !item.ends_at">-</p>
          <v-chip
            v-else
            :color="getExpirationStatusColor(item)"
            class="ma-2"
            outlined
          >
            {{ getExpirationStatus(item) }}
          </v-chip>
        </template>
        <!-- End:: Expiration Status -->

        <!-- Start:: Status -->
        <template v-slot:[`item.status_label`]="{ item }">
          <div class="activation" dir="ltr" style="z-index: 1">
            <v-switch
              class="mt-2"
              color="success"
              :input-value="item.is_active"
              :disabled="loading"
              @change="toggleStatus(item, $event)"
              hide-details
            ></v-switch>
          </div>
        </template>
        <!-- End:: Status -->

        <!-- Start:: Actions -->
        <template v-slot:[`item.actions`]="{ item }">
          <div class="actions">
            <!-- Activate Button (only for inactive coupons) -->
            <!-- <a-tooltip placement="bottom" v-if="!item.is_active">
              <template slot="title">
                <span>{{ $t("BUTTONS.activate") }}</span>
              </template>
              <button class="btn_activate" @click="selectActivateItem(item)">
                <i class="fad fa-check-circle"></i>
              </button>
            </a-tooltip> -->

            <!-- Deactivate Button (only for active coupons) -->
            <!-- <a-tooltip placement="bottom" v-if="item.is_active">
              <template slot="title">
                <span>{{ $t("BUTTONS.deactivate") }}</span>
              </template>
              <button
                class="btn_deactivate"
                @click="selectDeactivateItem(item)"
              >
                <i class="fad fa-times-circle"></i>
              </button>
            </a-tooltip> -->

            <a-tooltip placement="bottom">
              <template slot="title">
                <span>{{ $t("BUTTONS.delete") }}</span>
              </template>
              <button class="btn_delete" @click="selectDeleteItem(item)">
                <i class="fal fa-trash-alt"></i>
              </button>
            </a-tooltip>
          </div>
        </template>
        <!-- End:: Actions -->

        <!-- ======================== Start:: Dialogs ======================== -->
        <template v-slot:top>
          <!-- Start:: Delete Modal -->
          <v-dialog v-model="dialogDelete">
            <v-card>
              <v-card-title class="text-h5 justify-center" v-if="itemToDelete">
                {{ $t("TITLES.deleteCouponConfirm") }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmDeleteItem">{{
                  $t("BUTTONS.ok")
                }}</v-btn>

                <v-btn class="modal_cancel_btn" @click="dialogDelete = false">{{
                  $t("BUTTONS.cancel")
                }}</v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Delete Modal -->

          <!-- Start:: Activate Modal -->
          <v-dialog v-model="dialogActivate">
            <v-card>
              <v-card-title
                class="text-h5 justify-center"
                v-if="itemToActivate"
              >
                {{ $t("TITLES.activateCouponConfirm") }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmActivateItem">
                  {{ $t("BUTTONS.ok") }}
                </v-btn>

                <v-btn class="modal_cancel_btn" @click="dialogActivate = false">
                  {{ $t("BUTTONS.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Activate Modal -->

          <!-- Start:: Deactivate Modal -->
          <v-dialog v-model="dialogDeactivate">
            <v-card>
              <v-card-title
                class="text-h5 justify-center"
                v-if="itemToDeactivate"
              >
                {{ $t("TITLES.deactivateCouponConfirm") }}
              </v-card-title>
              <v-card-actions>
                <v-btn class="modal_confirm_btn" @click="confirmDeactivateItem">
                  {{ $t("BUTTONS.ok") }}
                </v-btn>

                <v-btn
                  class="modal_cancel_btn"
                  @click="dialogDeactivate = false"
                >
                  {{ $t("BUTTONS.cancel") }}
                </v-btn>
                <v-spacer></v-spacer>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Deactivate Modal -->

          <!-- Start:: Users Modal -->
          <v-dialog v-model="dialogUsers" max-width="800">
            <v-card>
              <v-card-title
                class="text-h5 justify-center"
                v-if="itemToViewUsers"
              >
                {{ $t("TITLES.couponUsers") }} - {{ itemToViewUsers.name }}
              </v-card-title>
              <v-card-text>
                <div v-if="loadingUsers" class="text-center py-5">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                  ></v-progress-circular>
                </div>
                <div v-else>
                  <v-data-table
                    :headers="usersTableHeaders"
                    :items="usersList"
                    :items-per-page="10"
                    class="elevation-1"
                    :loading="loadingUsers"
                    :loading-text="$t('TABLES.loadingData')"
                  >
                    <template v-slot:no-data>
                      {{ $t("TABLES.noData") }}
                    </template>
                    <template v-slot:[`item.id`]="{ index }">
                      {{ index + 1 }}
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                      {{
                        item.name ||
                        item.fullname ||
                        `${item.first_name || ""} ${
                          item.last_name || ""
                        }`.trim() ||
                        `User ${item.id}`
                      }}
                    </template>
                    <!-- <template v-slot:[`item.email`]="{ item }">
                      {{ item.email || '-' }}
                    </template> -->
                    <template v-slot:[`item.phone`]="{ item }">
                      {{ item.phone || item.mobile || "-" }}
                    </template>
                  </v-data-table>
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn class="modal_cancel_btn" @click="dialogUsers = false">
                  {{ $t("BUTTONS.close") }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <!-- End:: Users Modal -->
        </template>
        <!-- ======================== End:: Dialogs ======================== -->
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

export default {
  name: "AllCoupons",

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),

    statusOptions() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.active"),
          value: true,
        },
        {
          id: 2,
          name: this.$t("STATUS.notActive"),
          value: false,
        },
      ];
    },

    expirationStatusOptions() {
      return [
        {
          id: 1,
          name: this.$t("STATUS.notStartedYet"),
          value: "not_started",
        },
        {
          id: 2,
          name: this.$t("STATUS.onGoing"),
          value: "active",
        },
        {
          id: 3,
          name: this.$t("STATUS.expired"),
          value: "expired",
        },
      ];
    },
  },

  data() {
    return {
      // Start:: Loading Data
      loading: false,
      isWaitingRequest: false,
      // End:: Loading Data

      // Start:: Filter Data
      filterFormIsActive: false,
      filterOptions: {
        name: null,
        expirationStatus: null,
        is_active: null,
      },
      // End:: Filter Data

      // Start:: Table Data
      searchValue: "",
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          width: "80",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.couponName"),
          value: "name",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.usageCode"),
          value: "code",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.startDate"),
          value: "starts_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.endDate"),
          value: "ends_at",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.usageCount"),
          value: "usage_count",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.users"),
          value: "users",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.expirationStatus"),
          value: "expiration_status",
          sortable: false,
          align: "center",
        },
        {
          text: this.$t("PLACEHOLDERS.status"),
          value: "status_label",
          align: "center",
          sortable: false,
          width: "150",
        },
        {
          text: this.$t("TABLES.Admins.actions"),
          value: "actions",
          sortable: false,
          align: "center",
        },
      ],
      tableRows: [],
      // End:: Table Data

      // Start:: Dialogs Control Data
      dialogDelete: false,
      itemToDelete: null,
      dialogActivate: false,
      itemToActivate: null,
      dialogDeactivate: false,
      itemToDeactivate: null,
      dialogUsers: false,
      itemToViewUsers: null,
      loadingUsers: false,
      usersList: [],
      usersTableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.name"),
          value: "name",
          sortable: false,
          align: "center",
        },
        // {
        //   text: this.$t("PLACEHOLDERS.email"),
        //   value: "email",
        //   sortable: false,
        //   align: "center",
        // },
        {
          text: this.$t("PLACEHOLDERS.phone"),
          value: "phone",
          sortable: false,
          align: "center",
        },
      ],
      // End:: Dialogs Control Data

      // Start:: Pagination Data
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 15,
      },
      // End:: Pagination Data
    };
  },

  watch: {
    // Start:: Page Query Param Watcher To Get Page Data Based On It's Change
    ["$route.query.page"]() {
      this.paginations.current_page = +this.$route.query.page;
      this.setTableRows();
    },
    // End:: Page Query Param Watcher To Get Page Data Based On It's Change
  },

  methods: {
    // Start:: Format Date
    formatDate(date) {
      if (!date) return "-";
      return moment(date).format("YYYY-MM-DD");
    },
    // End:: Format Date

    // Start:: Get Expiration Status
    getExpirationStatus(item) {
      if (!item.starts_at || !item.ends_at) return "-";

      const now = moment();
      const startDate = moment(item.starts_at);
      const endDate = moment(item.ends_at);

      if (now.isBefore(startDate)) {
        return this.$t("STATUS.notStartedYet");
      } else if (now.isAfter(endDate)) {
        return this.$t("STATUS.expired");
      } else {
        return this.$t("STATUS.onGoing");
      }
    },
    // End:: Get Expiration Status

    // Start:: Get Expiration Status Class
    getExpirationStatusClass(item) {
      if (!item.starts_at || !item.ends_at) return "";

      const now = moment();
      const startDate = moment(item.starts_at);
      const endDate = moment(item.ends_at);

      if (now.isBefore(startDate)) {
        return "badge badge-info";
      } else if (now.isAfter(endDate)) {
        return "badge badge-danger";
      } else {
        return "badge badge-success";
      }
    },
    // End:: Get Expiration Status Class

    // Start:: Get Expiration Status Color
    getExpirationStatusColor(item) {
      if (!item.starts_at || !item.ends_at) return "grey";

      const now = moment();
      const startDate = moment(item.starts_at);
      const endDate = moment(item.ends_at);

      if (now.isBefore(startDate)) {
        return "info";
      } else if (now.isAfter(endDate)) {
        return "error";
      } else {
        return "success";
      }
    },
    // End:: Get Expiration Status Color

    // Start:: Handel Filter
    async submitFilterForm() {
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/coupons/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.name = null;
      this.filterOptions.expirationStatus = null;
      this.filterOptions.is_active = null;
      if (this.$route.query.page !== "1") {
        await this.$router.push({ path: "/coupons/all", query: { page: 1 } });
      }
      this.setTableRows();
    },
    // End:: Handel Filter

    // Start:: Set Table Rows
    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Coupons
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "coupons?per_page=10",
          params: {
            page: this.paginations.current_page,
            "filter[name]": this.filterOptions.name,
            "filter[expiry_status]": this.filterOptions.expirationStatus?.value,
            "filter[is_active]": this.filterOptions.is_active?.value,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data.map((item) => ({
          ...item,
          usage_count: item.usage_count || 0,
        }));
        this.paginations.last_page = res.data.pagination?.last_page || 1;
        this.paginations.items_per_page = res.data.pagination?.per_page || 15;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // End:: Set Table Rows

    // ==================== Start:: Crud ====================
    // ===== Start:: Activate
    selectActivateItem(item) {
      this.dialogActivate = true;
      this.itemToActivate = item;
    },
    async confirmActivateItem() {
      try {
        await this.$axios({
          method: "PATCH",
          url: `coupons/${this.itemToActivate.id}/activate`,
        });
        this.dialogActivate = false;
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.activatedSuccessfully"));
      } catch (error) {
        this.dialogActivate = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // ===== End:: Activate

    // ===== Start:: Deactivate
    selectDeactivateItem(item) {
      this.dialogDeactivate = true;
      this.itemToDeactivate = item;
    },
    async confirmDeactivateItem() {
      try {
        await this.$axios({
          method: "PATCH",
          url: `coupons/${this.itemToDeactivate.id}/deactivate`,
        });
        this.dialogDeactivate = false;
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.deactivatedSuccessfully"));
      } catch (error) {
        this.dialogDeactivate = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // ===== End:: Deactivate

    // ===== Start:: Delete
    selectDeleteItem(item) {
      this.dialogDelete = true;
      this.itemToDelete = item;
    },
    async confirmDeleteItem() {
      try {
        await this.$axios({
          method: "DELETE",
          url: `coupons/${this.itemToDelete.id}`,
        });
        this.dialogDelete = false;
        this.setTableRows();
        this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
      } catch (error) {
        this.dialogDelete = false;
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // ===== End:: Delete

    // ===== Start:: Toggle Status
    async toggleStatus(item, newValue) {
      try {
        if (newValue) {
          // Activate
          await this.$axios({
            method: "PATCH",
            url: `coupons/${item.id}/activate`,
          });
          this.$message.success(this.$t("MESSAGES.activatedSuccessfully"));
        } else {
          // Deactivate
          await this.$axios({
            method: "PATCH",
            url: `coupons/${item.id}/deactivate`,
          });
          this.$message.success(this.$t("MESSAGES.deactivatedSuccessfully"));
        }
        // Refresh table data to reflect the change
        this.setTableRows();
      } catch (error) {
        // Refresh table data to revert the UI state
        this.setTableRows();
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      }
    },
    // ===== End:: Toggle Status

    // ===== Start:: View Users
    selectViewUsers(item) {
      this.dialogUsers = true;
      this.itemToViewUsers = item;
      this.getCouponUsers(item.id);
    },
    async getCouponUsers(couponId) {
      this.loadingUsers = true;
      this.usersList = [];
      try {
        let res = await this.$axios({
          method: "GET",
          url: `coupons/${couponId}`,
        });
        const coupon = res.data.data;

        // Extract users from coupon data
        if (coupon.users && Array.isArray(coupon.users)) {
          this.usersList = coupon.users.map((user) => ({
            id: typeof user === "object" ? user.id : user,
            name:
              typeof user === "object"
                ? user.name ||
                  user.fullname ||
                  `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                  `User ${user.id}`
                : `User ${user}`,
            email: typeof user === "object" ? user.email || "-" : "-",
            phone:
              typeof user === "object" ? user.phone || user.mobile || "-" : "-",
            fullname:
              typeof user === "object"
                ? user.fullname ||
                  `${user.first_name || ""} ${user.last_name || ""}`.trim()
                : null,
            first_name: typeof user === "object" ? user.first_name : null,
            last_name: typeof user === "object" ? user.last_name : null,
            mobile: typeof user === "object" ? user.mobile : null,
          }));
        }
      } catch (error) {
        console.log(error.response?.data?.message);
        this.$message.error(
          error.response?.data?.message || this.$t("MESSAGES.errorOccurred")
        );
      } finally {
        this.loadingUsers = false;
      }
    },
    // ===== End:: View Users
    // ==================== End:: Crud ====================
  },

  created() {
    // Start:: Fire Methods
    window.addEventListener("click", () => {
      this.filterFormIsActive = false;
    });
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
    // End:: Fire Methods
  },
};
</script>

<style scoped>
.btn_view_users {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.btn_view_users:hover:not(:disabled) {
  background-color: #1976d2;
}

.btn_view_users:disabled {
  background-color: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn_view_users i {
  font-size: 16px;
}

.btn_view_users span {
  font-weight: 500;
}
</style>
